/**
 * AccountDecisionPage -- the per-account Tempo screen: one account, one score
 * date, the feature vector the engine read and the recommendation it produced.
 *
 * Routes: /tempo/accounts (account-id lookup) and /tempo/accounts/:accountId.
 * The contract declares no list operation over accounts, so the entry point is
 * a lookup field rather than a roster; a roster would need a path that does not
 * exist.
 *
 * Reads (tempo.openapi.yaml):
 *   GET /api/v1/tempo/accounts/{accountId}/features        getAccountFeatures
 *   GET /api/v1/tempo/accounts/{accountId}/recommendation  getCoverageRecommendation
 * and, for the institution's name only (platform-admin.openapi.yaml):
 *   GET /api/admin/tenants/{tenantId}                      TenantDetail.name
 *
 * SCOPE LIMIT. tempo-design.md Section 12.3 specifies a decision
 * resolver that also names the winning rung of the precedence ladder, the rungs
 * it beat, and the counterfactual that would move the outcome. No element of
 * either contract carries a rung or a counterfactual, so this page shows what
 * the contracts do carry -- policy version, the cure-cap basis the engine
 * resolved to, feature snapshot, assigned limit, ordered reason codes -- and
 * says so on the page rather than implying the resolver exists.
 *
 * Reason codes render as the spec enum value. Schema `LiquidityReasonCode`
 * carries one description for the vocabulary as a whole and none per member, so
 * there is no per-code sentence to render and this page does not author one: an
 * invented gloss on an examiner-legible code is a fabricated label on the
 * surface that most needs to be literal.
 *
 * The advisory boundary is on the screen because it is in the contract -- the
 * `getCoverageRecommendation` operation's description says the FI's pay/no-pay
 * decision is authoritative and this output is an input to it.
 *
 * AN ABSENCE STATE RENDERS ONLY FOR A 404. Each read resolves a 404 to null
 * data and throws on every other non-2xx, so each absence card is guarded on
 * its own read's error being null as well. A failed read renders its error
 * alert and no absence card: "No recommendation recorded" on a 503 states a
 * recorded state nobody wrote, which is the defect delivery-mode.ts and this
 * screen both exist to avoid.
 */

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  BodyText,
  Button,
  Card,
  EmptyState,
  Heading,
  Input,
  Skeleton,
  Badge,
  type BadgeProps,
} from '@aaimplatform/ui';
import {
  useAccountFeatures,
  useCoverageRecommendation,
  useDownloadAccountEvidence,
  type CoverageRecommendation,
  type LiquidityFeatureVector,
} from '../../hooks/useTempoAccount';
import { deliveryModeBadgeStatus, deliveryModeLabel } from './delivery-mode';
import { BADGE_TONE } from '../collateral/CollateralListParts';
import { tenantsApi } from '../../api/services/tenants';

/**
 * The name of the institution the server stamped on the snapshot, read from
 * GET /tenants/{tenantId} (platform-admin.openapi.yaml, TenantDetail.name).
 * The id itself is never rendered (GH #678, operator ruling 2026-10-23): a
 * reader who cannot resolve a UUID gains nothing from it. The handler lets an
 * FI operator read only their own tenant and answers 403 otherwise, so a
 * refusal, a failure or a pending read all resolve to no name, and the page
 * shows nothing in that slot rather than the id.
 */
function useRecordedInstitutionName(tenantId: string | undefined): string | undefined {
  const { data } = useQuery({
    queryKey: ['admin', 'tenants', 'detail', tenantId],
    enabled: typeof tenantId === 'string' && tenantId.length > 0,
    queryFn: () => tenantsApi.get(tenantId as string),
    staleTime: 5 * 60 * 1000,
    // tenantsApi.get throws an Error that carries no status, so the app-wide
    // retry predicate cannot tell a 403 from a transient failure. A missing
    // name costs one line of chrome; three retries on a refusal cost requests.
    retry: false,
  });
  const name = data?.name.trim();
  return name ? name : undefined;
}

/** Query-string key carrying the optional scoreDate the features path accepts. */
const SCORE_DATE_PARAM = 'scoreDate';

const ABSENT = '--';

/**
 * The text a recommendation carries when `cureCapBasis` is absent.
 * `CoverageRecommendation.cureCapBasis` is OPTIONAL and an omitted value means
 * NOT RECORDED -- written before spec 0.6.0, or by an engine image that
 * predates it under a later version. The spec is explicit that it
 * does NOT mean any particular basis, and that a reader must not resolve it
 * through the absence rule on LiquidityPolicyParameters.cureCapBasis: that rule
 * says what a POLICY VERSION's absent field means, and a stored row cannot
 * change what it meant. So this screen states the gap and never a basis name.
 */
const CURE_CAP_BASIS_UNRECORDED_LABEL = 'Not recorded';

/** Label for a possibly-absent basis. Never returns an invented basis. */
function cureCapBasisLabel(basis: CoverageRecommendation['cureCapBasis']): string {
  return basis ?? CURE_CAP_BASIS_UNRECORDED_LABEL;
}

/**
 * Every cause the engine collapses into one 404, read off the service rather
 * than condensed to the flattering three. The flag gate
 * (tempo-engine middleware/feature-flag-gate.ts) answers 404 when the
 * institution does not carry the overdraft-preview entitlement, when the
 * request arrives with no tenant on the session, and when its own entitlement
 * read throws and it closes. Each route handler (features/route-handlers.ts,
 * decision-support/route-handlers.ts) answers 404 for a malformed account
 * identifier, for an account on another institution's books, and for a missing
 * row. The contract's NotFound response distinguishes none of them, so the
 * page names each one rather than the three that read best.
 */
const NOTHING_RECORDED_CAUSES = [
  'Tempo returned no feature snapshot and no recommendation for this account,',
  'and answers 404 for every cause without distinguishing them: your institution',
  'does not carry the overdraft preview entitlement, the session carried no',
  'institution, the entitlement read failed and the gate closed, the account',
  'identifier is not a UUID, the account belongs to another institution, the',
  'account has never been scored, or no snapshot exists on the requested score',
  'date.',
].join(' ');

/**
 * Tier badge variant. All four tier values are truthy strings, so a boolean test
 * over the field renders "active" for NONE -- the defect delivery-mode.ts was
 * extracted to prevent. NONE is a real recorded outcome and reads as neutral,
 * never as a missing value.
 */
function tierBadgeVariant(tier: CoverageRecommendation['tier']): NonNullable<BadgeProps['variant']> {
  return tier === 'NONE' ? 'neutral' : 'success';
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function formatUsd(value: number | undefined): string {
  if (value === undefined) return ABSENT;
  return usdFormatter.format(value);
}

function formatCount(value: number | undefined): string {
  if (value === undefined) return ABSENT;
  return value.toLocaleString('en-US');
}

function formatRatio(value: number | undefined): string {
  if (value === undefined) return ABSENT;
  return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

function formatBoolean(value: boolean | undefined): string {
  if (value === undefined) return ABSENT;
  return value ? 'Yes' : 'No';
}

function formatDate(value: string | undefined): string {
  if (!value) return ABSENT;
  // A date-only value parsed by Date() lands at UTC midnight; rendering it in
  // the viewer's zone can move it a day. Split the ISO date instead.
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${year}-${month}-${day}`;
}

/**
 * Pinned to UTC and labeled. Rendered in the viewer's zone, one `computedAt`
 * reads as two different clock times to two operators in two zones, and neither
 * sees which zone they are reading. This is an examiner-facing surface, so the
 * instant is stated once, in the zone the engine stamps it in.
 */
function formatDateTime(value: string | undefined): string {
  if (!value) return ABSENT;
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

/**
 * THIS PAGE'S OWN READING ORDER, not the contract's. Schema
 * `LiquidityFeatureVector` is a flat property list: it declares no groups, no
 * sub-objects and no `x-` grouping extension, so there is no taxonomy in the
 * arbiter to cite. The families are the ones docs/architecture/tempo-design.md
 * describes under "Feature vector", and this page diverges from that source in
 * three places, named so the difference is not read as the design's: "Health
 * economics" renders as "Economics", "Confidence" renders as "Data quality",
 * and `directDepositActive` sits under Deposit cadence rather than under
 * Relationship. Within each group the measures are in spec declaration order.
 *
 * The USD kinds are the fields the tempo-engine limit function
 * (services/tempo-engine/src/scorecard/limit-function.ts) consumes as dollars
 * against dollar-denominated caps; a field whose unit the spec does not declare
 * is rendered as a plain number with whatever note the spec carries.
 */
const MEASURE_GROUPS: ReadonlyArray<{
  title: string;
  measures: ReadonlyArray<{
    /** The spec property name, rendered verbatim -- these are the examiner's handles. */
    key: keyof LiquidityFeatureVector;
    /** How the value is formatted. The page's presentation choice; the contract carries no such field. */
    kind: 'usd' | 'count' | 'ratio' | 'boolean';
    /** The spec's own description, where it carries one. Never authored here. */
    note?: string;
  }>;
}> = [
  {
    title: 'Deposit cadence',
    measures: [
      { key: 'depositCount30d', kind: 'count' },
      { key: 'depositTotal30d', kind: 'usd' },
      { key: 'medianDepositAmount90d', kind: 'usd' },
      { key: 'recurringInflowScore', kind: 'ratio' },
      { key: 'incomeStabilityCoefficient', kind: 'ratio' },
      { key: 'directDepositActive', kind: 'boolean' },
    ],
  },
  {
    title: 'Balance behavior',
    measures: [
      { key: 'averageDailyBalance30d', kind: 'usd' },
      { key: 'averageDailyBalance90d', kind: 'usd' },
      { key: 'minBalanceP5In90d', kind: 'usd' },
      { key: 'balanceTrendSlope90d', kind: 'ratio', note: 'USD per day, linear fit' },
    ],
  },
  {
    title: 'Flow coverage',
    measures: [
      // The three cure-cap measures read together, and the notes are the
      // LEADING SENTENCE of each spec description, quoted rather than
      // condensed. The two medians are the same number for an account whose
      // every day nets positive and are far apart for one paid twice a month,
      // so a reader needs the count to tell which case they are looking at.
      {
        key: 'netFlowMedianDaily30d',
        kind: 'usd',
        note: 'Median of the thirty daily net-flow values over the posted-only 30-day window.',
      },
      {
        key: 'netFlowMedianPositiveDays30d',
        kind: 'usd',
        note: 'Median of the daily net-flow values strictly greater than zero, over the same 30-day window and the same posted-only sample as netFlowMedianDaily30d.',
      },
      {
        key: 'netFlowPositiveDayCount30d',
        kind: 'count',
        note: 'How many of the thirty days netted positive, and the denominator netFlowMedianPositiveDays30d is read against.',
      },
      { key: 'inflowOutflowRatio90d', kind: 'ratio' },
    ],
  },
  {
    title: 'Stress markers',
    measures: [
      { key: 'daysNegative90d', kind: 'count' },
      { key: 'maxConsecutiveNegativeDays180d', kind: 'count' },
      {
        key: 'cureTimeDays',
        kind: 'ratio',
        note: 'Median days from negative balance to restored positive',
      },
      { key: 'daysSinceLastNegative', kind: 'count' },
      { key: 'nsfEventCount90d', kind: 'count' },
      { key: 'deniedDebitCount90d', kind: 'count' },
    ],
  },
  {
    title: 'Relationship',
    measures: [
      { key: 'accountTenureDays', kind: 'count' },
      { key: 'relationshipBreadth', kind: 'count' },
    ],
  },
  {
    title: 'Economics',
    measures: [{ key: 'feeToInflowRatio90d', kind: 'ratio' }],
  },
  {
    title: 'Data quality',
    measures: [
      { key: 'dataCompletenessRatio', kind: 'ratio' },
      { key: 'providerFreshnessHours', kind: 'ratio' },
    ],
  },
];

function renderMeasure(
  features: LiquidityFeatureVector,
  measure: (typeof MEASURE_GROUPS)[number]['measures'][number],
): string {
  const raw = features[measure.key];
  switch (measure.kind) {
    case 'usd':
      return formatUsd(typeof raw === 'number' ? raw : undefined);
    case 'count':
      return formatCount(typeof raw === 'number' ? raw : undefined);
    case 'ratio':
      return formatRatio(typeof raw === 'number' ? raw : undefined);
    case 'boolean':
      return formatBoolean(typeof raw === 'boolean' ? raw : undefined);
  }
}

/** One label/value row. An absent optional measure reads as ABSENT, never as zero. */
function MeasureRow({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-recessed last:border-b-0">
      <div className="min-w-0">
        <BodyText variant="ui" className="text-sm text-secondary font-mono break-words">
          {label}
        </BodyText>
        {note && (
          <BodyText variant="ui" className="text-xs text-tertiary">
            {note}
          </BodyText>
        )}
      </div>
      <BodyText variant="ui" className="text-sm text-primary font-mono tabular-nums shrink-0">
        {value}
      </BodyText>
    </div>
  );
}

/** The lookup entry point for /tempo/accounts. */
function AccountLookup() {
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState('');
  const trimmed = accountId.trim();

  return (
    <div className="space-y-6">
      <div>
        {/* Level 2: PortalLayout's AppHeader renders the page h1 (operator ruling on GH #2664, 2026-08-20). */}
        <Heading level={2} size="page">Account Decision</Heading>
        <BodyText variant="ui" className="text-tertiary text-sm mt-1">
          Open one account to see the feature snapshot the scorecard read and the advisory
          recommendation it produced. Your institution makes every pay, no-pay and credit
          decision.
        </BodyText>
      </div>

      <Card className="p-6 max-w-content-narrow">
        <Heading level={2} size="card" className="font-sans mb-4">Find an account</Heading>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            // react-router 7 types navigate() as void | Promise<void>; the
            // navigation is fire-and-forget here and there is nothing to await.
            if (trimmed) void navigate(`/tempo/accounts/${encodeURIComponent(trimmed)}`);
          }}
          className="space-y-4"
        >
          <Input
            label="Account identifier"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
            helperText="The consumer-domain FinancialAccount id. Tempo declares no operation that lists accounts, so the identifier comes from the account itself."
          />
          <Button type="submit" disabled={!trimmed}>
            Open account
          </Button>
        </form>
      </Card>
    </div>
  );
}

/** The ordered reason codes, rendered as the closed vocabulary's own values. */
function ReasonCodeList({ recommendation }: { recommendation: CoverageRecommendation }) {
  if (recommendation.reasonCodes.length === 0) {
    return (
      <BodyText variant="ui" className="text-sm text-secondary">
        This recommendation records no reason codes.
      </BodyText>
    );
  }
  return (
    <ol className="space-y-2">
      {recommendation.reasonCodes.map((code, index) => (
        <li key={`${code}-${index}`} className="flex items-baseline gap-3">
          <span className="text-xs text-tertiary font-mono tabular-nums shrink-0">
            {index + 1}
          </span>
          <span className="text-sm text-primary font-mono break-words">{code}</span>
        </li>
      ))}
    </ol>
  );
}

function RecommendationCard({ recommendation }: { recommendation: CoverageRecommendation }) {
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <Heading level={2} size="card" className="font-sans">Recommendation</Heading>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant={tierBadgeVariant(recommendation.tier)} size="small">
            {recommendation.tier}
          </Badge>
          <Badge variant={BADGE_TONE[deliveryModeBadgeStatus(recommendation.deliveryMode)]} size="small">
            {deliveryModeLabel(recommendation.deliveryMode)}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <div>
          <MeasureRow label="tier" value={recommendation.tier} />
          <MeasureRow label="limitAmount" value={formatUsd(recommendation.limitAmount)} />
          {/* A version is an identifier, not a quantity: formatCount would
              render version 1000 as "1,000" and the grouped form matches no
              policy the chassis issued. */}
          <MeasureRow label="policyVersion" value={String(recommendation.policyVersion)} />
          <MeasureRow
            label="deliveryMode"
            value={deliveryModeLabel(recommendation.deliveryMode)}
            note={
              recommendation.deliveryMode
                ? undefined
                : 'The mode in force was not recorded at computation. Not an OFF.'
            }
          />
          {/* Rendered as the spec's own enum value, for the reason the reason
              codes are: the basis is the handle an examiner re-derives the
              limit from, and a readable gloss authored here would be a label
              they cannot trace to LiquidityCureCapBasis. */}
          <MeasureRow
            label="cureCapBasis"
            value={cureCapBasisLabel(recommendation.cureCapBasis)}
            note={
              recommendation.cureCapBasis
                ? undefined
                : 'The basis was not recorded at computation. Not a claim about which basis ran.'
            }
          />
        </div>
        <div>
          <MeasureRow label="scoreDate" value={formatDate(recommendation.scoreDate)} />
          <MeasureRow label="computedAt" value={formatDateTime(recommendation.computedAt)} />
          <MeasureRow label="confidence" value={formatRatio(recommendation.confidence)} />
          <MeasureRow
            label="chronicUseFlag"
            value={formatBoolean(recommendation.chronicUseFlag)}
          />
          <MeasureRow
            label="graduationEligible"
            value={formatBoolean(recommendation.graduationEligible)}
          />
        </div>
      </div>

      <div className="mt-6">
        <Heading level={3} size="card" className="font-sans text-sm mb-2">
          Reason codes, in order
        </Heading>
        <BodyText variant="ui" className="text-xs text-tertiary mb-3">
          The closed vocabulary declared as LiquidityReasonCode. The codes are shown as the
          contract spells them, and their order is the order the engine emitted.
        </BodyText>
        <ReasonCodeList recommendation={recommendation} />
      </div>

      <div className="mt-6 pt-4 border-t border-recessed">
        <MeasureRow
          label="featureSnapshotRef"
          value={recommendation.featureSnapshotRef}
          note="The feature vector this recommendation was computed from."
        />
      </div>
    </Card>
  );
}

function FeatureVectorPanel({ features }: { features: LiquidityFeatureVector }) {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <Heading level={2} size="card" className="font-sans mb-4">Feature snapshot</Heading>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8">
          <MeasureRow label="accountId" value={features.accountId} />
          <MeasureRow label="scoreDate" value={formatDate(features.scoreDate)} />
          <MeasureRow label="computedAt" value={formatDateTime(features.computedAt)} />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {MEASURE_GROUPS.map((group) => (
          <Card key={group.title} className="p-6">
            <Heading level={3} size="card" className="font-sans text-sm mb-3">
              {group.title}
            </Heading>
            {group.measures.map((measure) => (
              <MeasureRow
                key={String(measure.key)}
                label={String(measure.key)}
                value={renderMeasure(features, measure)}
                note={measure.note}
              />
            ))}
          </Card>
        ))}
      </div>
    </div>
  );
}

export function AccountDecisionPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scoreDate = searchParams.get(SCORE_DATE_PARAM) ?? '';

  const {
    data: features,
    isLoading: featuresLoading,
    error: featuresError,
  } = useAccountFeatures(accountId, scoreDate || undefined);
  const {
    data: recommendation,
    isLoading: recommendationLoading,
    error: recommendationError,
  } = useCoverageRecommendation(accountId);
  const evidenceDownload = useDownloadAccountEvidence();
  // tenantId is required on both response schemas. It is read off whichever
  // response arrived rather than off the session, so the screen names the
  // institution the SERVER stamped on the snapshot. Only its name renders.
  const recordedTenantId = features?.tenantId ?? recommendation?.tenantId;
  const recordedInstitutionName = useRecordedInstitutionName(recordedTenantId);

  if (!accountId) return <AccountLookup />;

  const isLoading = featuresLoading || recommendationLoading;
  // Both hooks resolve a 404 to null data and throw on every other non-2xx, so
  // an absence state is claimable only when the matching error is also null.
  // A 500 rendered as "no snapshot recorded" asserts a recorded state nobody
  // wrote, on a page an examiner reads.
  const nothingRecorded = !isLoading && !features && !recommendation;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Level 2: PortalLayout's AppHeader renders the page h1 (operator ruling on GH #2664, 2026-08-20). */}
          <Heading level={2} size="page">Account Decision</Heading>
          <BodyText variant="ui" className="text-tertiary text-sm mt-1 font-mono break-words">
            {accountId}
          </BodyText>
          {recordedInstitutionName && (
            <BodyText variant="ui" className="text-tertiary text-xs mt-1 break-words">
              Institution: {recordedInstitutionName}
            </BodyText>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button
            variant="secondary"
            onClick={() => evidenceDownload.mutate(accountId)}
            disabled={evidenceDownload.isPending}
          >
            {evidenceDownload.isPending ? 'Exporting...' : 'Download examiner evidence'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              void navigate('/tempo/accounts');
            }}
          >
            Look up another account
          </Button>
        </div>
      </div>

      {evidenceDownload.isError && (
        <Alert type="error" title="Evidence export failed">
          {evidenceDownload.error.code === 'ACCOUNT_NOT_FOUND'
            ? 'No scored decision for this account is on record for your institution. The export covers accounts the engine scored while the member was enrolled in Tempo.'
            : evidenceDownload.error.code === 'POLICY_NOT_FOUND'
              ? 'No Tempo policy version has been published yet, so there is no evidence package to export.'
              : evidenceDownload.error.message}
        </Alert>
      )}

      <BodyText variant="ui" className="text-sm text-secondary">
        The recommendation is advisory. Your institution&apos;s pay or no-pay decision is
        authoritative, and this screen is an input to it.
      </BodyText>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Input
            label="Score date"
            type="date"
            value={scoreDate}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) next.set(SCORE_DATE_PARAM, event.target.value);
              else next.delete(SCORE_DATE_PARAM);
              setSearchParams(next, { replace: true });
            }}
            helperText="Addresses one immutable feature snapshot. Blank returns the latest."
            fullWidth={false}
          />
          {scoreDate && (
            <Button
              variant="secondary"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete(SCORE_DATE_PARAM);
                setSearchParams(next, { replace: true });
              }}
            >
              Latest
            </Button>
          )}
        </div>
        <BodyText variant="ui" className="text-xs text-tertiary mt-3">
          The recommendation path declares no date parameter and always answers with the
          current recommendation; compare its featureSnapshotRef against the snapshot below
          before reading the two as one decision.
        </BodyText>
      </Card>

      <BodyText variant="ui" className="text-xs text-tertiary">
        The precedence rung that produced this outcome, and the counterfactual that would
        move it, are described in the Tempo design and carried by no element of either
        contract, so neither is shown here.
      </BodyText>

      {featuresError && (
        <Alert type="error" title="Feature snapshot load failed">
          {featuresError.message}
        </Alert>
      )}
      {recommendationError && (
        <Alert type="error" title="Recommendation load failed">
          {recommendationError.message}
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {nothingRecorded && !featuresError && !recommendationError && (
        <EmptyState title="Nothing recorded" description={NOTHING_RECORDED_CAUSES} />
      )}

      {!isLoading && recommendation && <RecommendationCard recommendation={recommendation} />}

      {/* A completed 404 is stated in every outcome cell where it happened, including the
          two where the OTHER read failed: the failed half carries its Alert above and this
          half still says what it found. */}
      {!isLoading && !recommendation && !recommendationError && (features || featuresError) && (
        <EmptyState
          title="No recommendation recorded"
          description={
            features
              ? 'A feature snapshot exists for this account and no recommendation was returned.'
              : 'No recommendation was returned for this account.'
          }
        />
      )}

      {!isLoading && features && <FeatureVectorPanel features={features} />}

      {!isLoading && !features && !featuresError && (recommendation || recommendationError) && (
        <EmptyState
          title="No feature snapshot recorded"
          description={
            recommendation
              ? `The recommendation above cites featureSnapshotRef ` +
                `${recommendation.featureSnapshotRef}, and no snapshot was returned for this ` +
                `account${scoreDate ? ` on ${formatDate(scoreDate)}` : ''}.`
              : `No feature snapshot was returned for this account` +
                `${scoreDate ? ` on ${formatDate(scoreDate)}` : ''}.`
          }
        />
      )}
    </div>
  );
}
